// index.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import DialogBox from '../DialogBox'

export type ConfirmationButton = 'Confirm' | 'OK' | 'Okay' | 'Dismiss'

/**
 * Props for the {@link ConfirmationDialogBox} component
 */
export interface ConfirmationDialogBoxProps {
  /** Controls whether the dialog is visible */
  open: boolean
  /** The title displayed at the top of the dialog */
  title: string
  /** The descriptive text shown in the dialog */
  description: string
  /** Influences the text for the confirmation button (see {@link ConfirmationButton}) */
  buttonText?: ConfirmationButton
  /** Callback function invoked when the confirmation action is triggered */
  onConfirm: () => void
}

/**
 * A dialog box component used to receive acknowledgment from users before proceeding with an action
 *
 * @example
 * ```tsx
 * <ConfirmationDialogBox
 *   open={isDialogOpen}
 *   title="Delete Item"
 *   description="Are you sure you want to delete this item? This action cannot be undone."
 *   buttonText="Confirm"
 *   onConfirm={handleDelete}
 * />
 * ```
 */
export function ConfirmationDialogBox(
  {
    open,
    title,
    description,
    buttonText,
    onConfirm
  }: Readonly<ConfirmationDialogBoxProps>
): JSX.Element {
  return (
    <DialogBox
      open={open}
      title={title}
      description={description}
      actionAlignment="center"
      actions={(
        <Stack
          direction="row"
          justifyContent="center"
        >
          <Button
            variant="contained"
            onClick={onConfirm}
            fullWidth
          >
            {buttonText}
          </Button>
        </Stack>
      )}
    />
  )
}

ConfirmationDialogBox.defaultProps = {
  buttonText: 'Confirm'
}
